import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import * as orderService from '../services/order/orderService';
import type { OrderDecryptField } from '../types/order/order';

/**
 * 配送地址明文 JSON 解析后的结构。
 *
 * 库中 `wms_order.delivery_address` 存的是**整段 JSON 的 RSA 密文**，
 * 解密后原样返回 JSON 串，由前端 parse。全部字段均可能缺失，故一律可选。
 */
export interface OrderDeliveryAddress {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  mobileNo?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  subDistrict?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postalCode?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  deliveryNote?: string | null;
}

export interface UseOrderPiiRevealResult {
  /** 是否已揭示明文。false 时调用方必须渲染掩码/占位，不得渲染任何原始值 */
  revealed: boolean;
  /** 解密请求进行中 */
  loading: boolean;
  /** 买家手机号明文；仅在 `revealed` 为 true 时有意义（可能为 null，表示该订单本就无手机号） */
  phone: string | null;
  /**
   * 配送地址明文对象；仅在 `revealed` 为 true 时有意义。
   * 为 null 表示「无地址」或「JSON 解析失败」——两种情况调用方都应渲染 '-' 或不可用提示，
   * **绝不可回退渲染原始串**（原始串在真实环境下是 684 字符密文，见 design D7）。
   */
  delivery: OrderDeliveryAddress | null;
  /** 切换揭示/收起。已揭示时收起（清空已取到的明文）；未揭示时发起解密请求 */
  toggle: () => void;
  /** 强制收起并清空明文（用于外部驱动的重置场景） */
  reset: () => void;
}

/**
 * 解析配送地址明文 JSON 串。
 *
 * - 兼容历史上地址可能嵌套在 `value` 属性下的形状（`{ value: {...} }`）
 * - **解析失败返回 null，绝不回退成 `{ addressLine1: 原始串 }`**：
 *   真实环境下原始串是 RSA 密文（约 684 字符 Base64），回退渲染等于把密文直接
 *   泼到界面上，既无信息价值又违背「明文按需揭示」的设计意图（design D7）
 */
function parseDeliveryAddress(raw: string | null | undefined): OrderDeliveryAddress | null {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    // 地址可能嵌套在 value 属性下
    const value = (parsed as { value?: unknown }).value;
    const result = (value && typeof value === 'object' ? value : parsed) as OrderDeliveryAddress;
    return result;
  } catch (e) {
    return null;
  }
}

/**
 * 订单 PII（手机号 + 配送地址）按需揭示 Hook。
 *
 * `wms_order` 的 `buyer_phone` 与 `delivery_address` 在库中均为 RSA 密文，
 * 列表/详情等常规查询接口返回的都是密文，直接渲染会显示乱码。
 * 本 Hook 默认保持掩码态，仅在调用 `toggle()` 时请求
 * `POST /api/v1/order/order/decrypt-detail`（两项明文的唯一出口），
 * **一次请求同时拿到手机号与地址**，无需分两次调用。
 *
 * ## 状态语义
 * Hook 自持状态，因此可直接用于表格行内：每个实例独立揭示/收起、互不影响；
 * 组件卸载（如翻页）时状态随之销毁，自动回到掩码态。
 * 额外地，`orderNo` 变化时会主动重置为掩码态，避免切换订单后仍展示上一单的明文。
 *
 * ## 使用约定
 * 调用方需自行判断原始值是否为空：为空时应直接渲染 '-'，不要挂载揭示控件，
 * 避免对没有手机号/地址的订单显示小眼睛并触发无意义的解密请求。
 *
 * @param orderNo 订单号，解密接口的唯一入参（仓库码后端从登录上下文取）
 * @param decryptFields 需要解密的字段列表，默认同时解密手机号与地址
 */
export function useOrderPiiReveal(
  orderNo?: string | null,
  decryptFields: OrderDecryptField[] = ['BUYER_PHONE', 'DELIVERY_ADDRESS']
): UseOrderPiiRevealResult {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<OrderDeliveryAddress | null>(null);

  const reset = useCallback(() => {
    setRevealed(false);
    setPhone(null);
    setDelivery(null);
  }, []);

  // 订单切换时收起，防止把上一单的 PII 明文继续展示给用户
  useEffect(() => {
    reset();
  }, [orderNo, reset]);

  const toggle = useCallback(async () => {
    // 已揭示 → 收起回掩码态
    if (revealed) {
      reset();
      return;
    }
    // 解密进行中忽略重复点击，避免重复请求
    if (!orderNo || loading) return;
    setLoading(true);
    try {
      const res = await orderService.getDecryptedOrderDetail(orderNo, decryptFields);
      if (res?.success && res.data) {
        setPhone(res.data.buyerPhone ?? null);
        setDelivery(parseDeliveryAddress(res.data.deliveryAddress));
        setRevealed(true);
      } else {
        message.error(res?.errorMessage || t('common.message.operationFailed'));
      }
    } catch (e) {
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  }, [revealed, reset, orderNo, loading, t]);

  return { revealed, loading, phone, delivery, toggle, reset };
}

export default useOrderPiiReveal;
